import { Server } from "lucide-react";
import { DiscordChannel, GuildConfig } from "@/lib/api";
import { Section, Field } from "@/components/panel/Section";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { ChannelPicker } from "@/components/panel/ChannelPicker";

type Props = {
  config: GuildConfig;
  channels: DiscordChannel[];
  onChange: (v: GuildConfig["fivem"]) => void;
};

export function FiveMTab({ config, channels, onChange }: Props) {
  const cfg = config.fivem;
  const update = <K extends keyof typeof cfg>(k: K, v: (typeof cfg)[K]) =>
    onChange({ ...cfg, [k]: v });
  const text = channels.filter((ch) => ch.type === 0);

  return (
    <Section
      icon={Server}
      title="FiveM / RP server"
      description="Buyer-facing RP server info: connect link, WL/rules channels and automatic replies for common WL/connect questions."
      enabled={cfg.enabled}
      onToggle={(v) => update("enabled", v)}
    >
      <div className="rounded-lg border border-dashed border-border bg-muted/25 p-4 text-sm text-muted-foreground space-y-2">
        <p className="font-medium text-foreground">How it works</p>
        <p>
          <strong>No slash commands.</strong> Fill connect/rules/WL channels and messages; the bot can post your{" "}
          <strong>status message</strong> where configured and optionally <strong>auto-reply</strong> when normal
          messages match your keywords (good for “how do I connect / whitelist?”).
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <Field label="Server name">
          <Input value={cfg.server_name} onChange={(e) => update("server_name", e.target.value)} />
        </Field>
        <Field label="Connect URL / cfx.re">
          <Input value={cfg.connect_url} onChange={(e) => update("connect_url", e.target.value)} placeholder="cfx.re/join/xxxxxx" />
        </Field>
        <Field label="Rules channel">
          <ChannelPicker channels={text} value={cfg.rules_channel_id} onChange={(v) => update("rules_channel_id", v === "__none__" ? "" : v)} />
        </Field>
        <Field label="Whitelist channel">
          <ChannelPicker channels={text} value={cfg.wl_channel_id} onChange={(v) => update("wl_channel_id", v === "__none__" ? "" : v)} />
        </Field>
        <Field label="Status/info channel">
          <ChannelPicker channels={text} value={cfg.status_channel_id} onChange={(v) => update("status_channel_id", v === "__none__" ? "" : v)} />
        </Field>
      </div>

      <Field label="Status message" hint="Placeholders: {connect}, {server}, {rules_channel}, {wl_channel}">
        <Textarea rows={3} value={cfg.status_message} onChange={(e) => update("status_message", e.target.value)} />
      </Field>

      <div className="flex items-center justify-between rounded-lg border border-border p-3">
        <div>
          <div className="text-sm font-medium">Auto-reply to WL/connect questions</div>
          <div className="text-xs text-muted-foreground">Uses the keywords below in normal messages.</div>
        </div>
        <Switch checked={cfg.auto_reply_enabled} onCheckedChange={(v) => update("auto_reply_enabled", v)} />
      </div>

      <Field label="Auto-reply keywords" hint="Comma separated, e.g. wl, whitelist, connect, fivem">
        <Input
          value={(cfg.auto_reply_keywords || []).join(", ")}
          onChange={(e) =>
            update(
              "auto_reply_keywords",
              e.target.value.split(",").map((x) => x.trim()).filter(Boolean),
            )
          }
        />
      </Field>
      <Field label="Auto-reply message" hint="Placeholders: {connect}, {server}, {rules_channel}, {wl_channel}">
        <Textarea rows={3} value={cfg.auto_reply_message} onChange={(e) => update("auto_reply_message", e.target.value)} />
      </Field>
    </Section>
  );
}
